import { Chip, IconButton, Typography } from '@material-tailwind/react'
import truncate from 'lodash/truncate'
import { useTranslation } from 'react-i18next'
import { PiSpeakerX, PiTrash } from 'react-icons/pi'
import { Link } from 'react-router-dom'
import AgamiDraftChip from '../components/AgamiDraftChip'
import AgamiMediaTypeChip from '../components/AgamiMediaTypeChip'
import AgamiTypeChip from '../components/AgamiTypeChip'
import { useDarkMode } from '../contexts/DarkModeContext'
import { useAgamiRoutes } from '../hooks/useAgamiRoutes'
import { AgamiLibraryItem } from '../types/agami-library'

interface AgamiLibraryTableProps {
  items: AgamiLibraryItem[]
  onDelete: (agami: AgamiLibraryItem) => void
}

const AgamiStatus: React.FC<{ agami: AgamiLibraryItem }> = ({ agami }) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  if (agami.isDraft) {
    return <AgamiDraftChip spot={{ is_draft: true, isProposal: agami.isProposal }} />
  }
  return (
    <Chip
      size="sm"
      variant={darkMode ? 'filled' : 'ghost'}
      color="green"
      value={t('agamiLibrary.status.published')}
      className="inline-block"
    />
  )
}

const Languages: React.FC<{ agami: AgamiLibraryItem }> = ({ agami }) => {
  const { t } = useTranslation()
  if (!agami.baseLanguage) {
    return <span title={t('agamiLibrary.languages.undeclared')}>N/D</span>
  }
  return (
    <span>
      {agami.baseLanguage.toUpperCase()}
      {agami.additionalLanguagesCount > 0 ? ` +${agami.additionalLanguagesCount}` : ''}
    </span>
  )
}

const UpdatedAt: React.FC<{ value: string }> = ({ value }) => {
  const { i18n } = useTranslation()
  return (
    <>
      {new Intl.DateTimeFormat(i18n.resolvedLanguage ?? i18n.language, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }).format(new Date(value))}
    </>
  )
}

export const AgamiLibraryTable: React.FC<AgamiLibraryTableProps> = ({ items, onDelete }) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()
  const { agamiFormPath } = useAgamiRoutes()

  return (
    <>
      <div className="hidden lg:block overflow-x-auto rounded-lg border border-blue-gray-100 dark:border-primary-400">
        <table className="w-full min-w-[980px] text-left">
          <thead className="bg-blue-gray-500/30 dark:bg-primary-600">
            <tr>
              {(
                [
                  'name',
                  'status',
                  'activation',
                  'media',
                  'languages',
                  'experiences',
                  'visibility',
                  'updated',
                  'actions',
                ] as const
              ).map((column) => (
                <th key={column} className="p-3">
                  <Typography variant="small" className="font-bold dark:text-gray-100">
                    {t(`agamiLibrary.table.${column}`)}
                  </Typography>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((agami) => (
              <tr
                key={agami.id}
                className="even:bg-blue-gray-50/50 dark:even:bg-primary-500 hover:bg-white-yellow dark:hover:bg-primary-300"
                data-testclass="agami-library-row"
              >
                <td className="p-3 max-w-[260px]">
                  <Link
                    to={agamiFormPath(agami.id)}
                    className="font-black underline dark:text-white"
                    data-testclass="agami-library-name-link"
                  >
                    {agami.name}
                  </Link>
                  {agami.description && (
                    <Typography
                      variant="small"
                      className="truncate dark:text-gray-200"
                      title={agami.description}
                    >
                      {agami.description}
                    </Typography>
                  )}
                </td>
                <td className="p-3">
                  <AgamiStatus agami={agami} />
                </td>
                <td className="p-3">
                  <AgamiTypeChip agamiType={agami.activationType} />
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <AgamiMediaTypeChip mediaType={agami.mediaType} />
                    {agami.mediaExpectedButMissing && (
                      <span title={t('agami.missingAudio')} className="text-orange-700 dark:text-orange-300">
                        <PiSpeakerX className="w-4 h-4" />
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-3 dark:text-gray-100">
                  <Languages agami={agami} />
                </td>
                <td className="p-3 dark:text-gray-100">{agami.experienceCount}</td>
                <td className="p-3">
                  {agami.isPublic ? (
                    <Chip
                      size="sm"
                      variant={darkMode ? 'filled' : 'ghost'}
                      color="green"
                      value={t('agamiLibrary.visibility.public')}
                      className="inline-block"
                    />
                  ) : (
                    <span className="dark:text-gray-300">—</span>
                  )}
                </td>
                <td className="p-3 whitespace-nowrap dark:text-gray-100">
                  <UpdatedAt value={agami.updatedAt} />
                </td>
                <td className="p-3 text-right">
                  <IconButton
                    variant="text"
                    size="sm"
                    color="yellow"
                    aria-label={t('agamiLibrary.actions.delete', { name: agami.name })}
                    onClick={() => onDelete(agami)}
                    data-testclass="agami-library-delete"
                  >
                    <PiTrash className="text-primary dark:text-base-gray-500 text-[20px]" />
                  </IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-3 lg:hidden">
        {items.map((agami) => (
          <article
            key={agami.id}
            className="rounded-lg border border-blue-gray-100 dark:border-primary-400 p-3"
          >
            <div className="flex justify-between gap-3">
              <div className="min-w-0">
                <Link
                  to={agamiFormPath(agami.id)}
                  className="font-black underline dark:text-white"
                  data-testclass="agami-library-name-link"
                >
                  {agami.name}
                </Link>
                {agami.description && (
                  <Typography variant="small" className="dark:text-gray-200">
                    {truncate(agami.description, { length: 90, separator: ' ' })}
                  </Typography>
                )}
              </div>
              <IconButton
                variant="text"
                size="sm"
                color="yellow"
                aria-label={t('agamiLibrary.actions.delete', { name: agami.name })}
                onClick={() => onDelete(agami)}
                data-testclass="agami-library-delete"
              >
                <PiTrash className="text-primary dark:text-base-gray-500 text-[20px]" />
              </IconButton>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <AgamiStatus agami={agami} />
              <AgamiTypeChip agamiType={agami.activationType} />
              <AgamiMediaTypeChip mediaType={agami.mediaType} />
              {agami.mediaExpectedButMissing && (
                <PiSpeakerX className="w-4 h-4 text-orange-700 dark:text-orange-300" />
              )}
            </div>
            <Typography variant="small" className="mt-2 dark:text-gray-200">
              <Languages agami={agami} /> ·{' '}
              {t('agamiLibrary.mobile.experiences', { count: agami.experienceCount })}
              {agami.isPublic ? ` · ${t('agamiLibrary.visibility.public')}` : ''} ·{' '}
              <UpdatedAt value={agami.updatedAt} />
            </Typography>
          </article>
        ))}
      </div>
    </>
  )
}
